import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Form, FormikProvider, useFormik } from 'formik';
// @mui
import { Stack, IconButton, InputAdornment, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../../components/iconify';
import { PASSWORD_REGEX } from '../../../Constants/RegexConstants';
import { resetPassword } from '../../../services/resetPassword';

// ----------------------------------------------------------------------

export default function ResetPasswordForm() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const ResetPasswordSchema = Yup.object().shape({
        password: Yup.string()
            .required('Password is required')
            .matches(
                PASSWORD_REGEX,
                'Password should contain at least one number and one special character having length between 6 To 16 characters.'
            ),
        confirmPassword: Yup.string()
            .required('Confirm password is required')
            .oneOf([Yup.ref('password')], 'Passwords do not match')
    });

    const formik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: '',
        },
        validationSchema: ResetPasswordSchema,

        onSubmit: (values) => {
            const body = {...values};
            delete body.confirmPassword;
            resetPassword(token, body)
                .then(({ data }) => {
                    if (data?.status === "success") {
                        navigate('/auth/login', { replace: true });
                        toast.success("Password updated");
                    }
                })
                .catch(() => {
                    toast.error("Error while reset password")
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
    });

    const {
        errors,
        touched,
        handleSubmit,
        getFieldProps,
        isSubmitting,
        setSubmitting,
    } = formik;

    return (
        <>
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <TextField
                            name="password"
                            label="Password"
                            {...getFieldProps('password')}
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            type={showPassword ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            name="confirmPassword"
                            label="Confirm Password"
                            style={{ marginBottom: '24px' }}
                            {...getFieldProps('confirmPassword')}
                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            type={showConfirmPassword ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                            <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
                        Reset Password
                    </LoadingButton>
                </Form>
            </FormikProvider>
        </>
    );
}
